<template>
  <v-tooltip
    v-model="show"
    :right="resolvedDirection === 'right'"
    :left="resolvedDirection === 'left'"
    :top="resolvedDirection === 'top'"
    :bottom="resolvedDirection === 'bottom'"
    :attach="attach"
    :nudge-bottom="resolvedDirection === 'bottom' ? nudge : undefined"
    :nudge-top="resolvedDirection === 'top' ? nudge : undefined"
    :nudge-left="resolvedDirection === 'left' ? nudge : undefined"
    :nudge-right="resolvedDirection === 'right' ? nudge : undefined"
    :open-on-hover="hoverMode"
    content-class="tooltip"
  >
    <template v-slot:activator="{ on, attrs }">
      <span
        v-bind="attrs"
        v-on="combinedListeners(on)"
        @click.stop="handleClick"
        ref="activator"
      >
        <slot name="trigger" />
      </span>
    </template>
    <div
      ref="tooltipContent"
      @mouseenter="handleTooltipEnter"
      @mouseleave="handleTooltipLeave"
    >
      <slot name="text" />
    </div>
  </v-tooltip>
</template>

<script>
export default {
  props: {
    color: {
      type: String,
      default: 'primary'
    },
    direction: {
      type: String,
      default: 'bottom'
    },
    attach: {
      type: [Boolean, String],
      default: false
    },
    nudge: {
      type: [Number, String],
      default: 0
    },
    hoverMode: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      show: false,
      resolvedDirection: this.direction,
      hideTimeout: null
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('scroll', this.handleScroll, true);
    this.$root.$on('close-tooltips', this.closeTooltip);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('scroll', this.handleScroll, true);
    this.$root.$off('close-tooltips', this.closeTooltip);
  },
  watch: {
    show(val) {
      if (val) {
        this.$nextTick(() => this.updatePlacement());
      }
    }
  },
  methods: {
    toggleTooltip() {
      this.show = !this.show;
      if (this.show) {
        this.$root.$emit('close-tooltips', this._uid);
        this.$nextTick(() => this.updatePlacement());
      }
    },
    closeTooltip(id) {
      if (id !== this._uid) {
        this.show = false;
      }
    },
    handleClickOutside(event) {
      if (
        this.show &&
        !this.$refs.activator?.contains(event.target) &&
        !this.$refs.tooltipContent?.contains(event.target)
      ) {
        this.show = false;
      }
    },
    handleScroll(event) {
      if (!this.$refs.tooltipContent) return;

      if (this.$refs.tooltipContent.contains(event.target)) {
        return;
      }

      if (this.show) {
        this.show = false;
      }
    },
    handleClick() {
      if (!this.hoverMode) {
        this.toggleTooltip();
      }
    },
    combinedListeners(on) {
      if (!this.hoverMode) {
        return on;
      }
      return {
        ...on,
        mouseenter: (e) => {
          this.$root.$emit('close-tooltips', this._uid);
          this.clearHideTimer();
          this.show = true;
          if (on.mouseenter) on.mouseenter(e);
        },
        mouseleave: (e) => {
          this.startHideTimer(250);
        }
      };
    },
    handleTooltipEnter() {
      if (!this.hoverMode) return;
      this.show = true;
      this.clearHideTimer();
    },
    handleTooltipLeave() {
      if (!this.hoverMode) return;
      this.startHideTimer(350);
    },
    startHideTimer(delay = 1150) {
      this.clearHideTimer();
      this.hideTimeout = setTimeout(() => {
        this.show = false;
      }, delay);
    },
    clearHideTimer() {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
    },
    updatePlacement() {
      if (!this.$refs.activator || !this.$refs.tooltipContent) return;
      if (!['bottom', 'top'].includes(this.direction)) {
        this.resolvedDirection = this.direction;
        return;
      }

      const activatorRect = this.$refs.activator.getBoundingClientRect();
      const tooltipRect = this.$refs.tooltipContent.getBoundingClientRect();
      const spaceBelow = window.innerHeight - activatorRect.bottom;
      const spaceAbove = activatorRect.top;

      if (this.direction === 'bottom') {
        if (activatorRect.bottom > window.innerHeight * 0.7 && spaceAbove > 0) {
          this.resolvedDirection = 'top';
          return;
        }
        if (tooltipRect.height > spaceBelow && spaceAbove >= tooltipRect.height) {
          this.resolvedDirection = 'top';
          return;
        }
      }

      if (this.direction === 'top' && tooltipRect.height > spaceAbove && spaceBelow >= tooltipRect.height) {
        this.resolvedDirection = 'bottom';
        return;
      }

      this.resolvedDirection = this.direction;
    }
  }
};
</script>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  padding: 10px;
  border-radius: 2px;
  opacity: 0;
  min-width: 200px;
  max-width: 400px;
  color: black !important;
  transition: opacity 1s;
  position: absolute;
  z-index: 2147483647 !important;
  background: #ffffff;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  font-family: 'Lato', sans-serif !important;
}

.v-tooltip__content {
  pointer-events: initial;
}
</style>

<style>
.recipient-tooltip {
  max-height: 240px;
  overflow-y: auto;
  min-width: 200px;
}

.recipient-tooltip__item {
  font-size: 12px;
  color: #3F3F3F;
  padding: 4px 0;
  border-bottom: 1px solid #F0F0F0;
}

.recipient-tooltip__item:last-child {
  border-bottom: none;
}

.recipient-inactive {
  color: #F44336;
  font-weight: 600;
}

.recipient-inactive-label {
  margin-left: 4px;
}
</style>
